import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useUrlSearchParams } from "src/app/hooks";
import { inferPrintDateType, LuckyDrawModel, mergeLuckyDrawGeneralSource } from '../../../models/LuckyDraw.model';
import CampaignScreens from "../../../CampaignScreens";
import { useGetLuckyDrawDetailGeneralInfo } from "../../../providers/LuckyDraw.provider";


export function useLuckyDrawForm() {
    const [searchParams, setSearchParams] = useUrlSearchParams<'step' | 'id' | 'page' | 'limit'>()
    const params = useParams<{ id: string }>();
    const steps = ["General Info", "Prize", "Store Category", "Summary"];
    const [currentStep, setCurrentStep] = useState(1);
    const [currentId, setCurrentId] = useState('');
    const [currentDetail, setCurrentDetail] = useState<LuckyDrawModel>({});
    const [initialPrintDateType, setInitialPrintDateType] = useState<'MAX_DAY_TO_PRINT' | 'FIX_DATE' | undefined>();
    const history = useHistory();

    const detailId = currentId || (params.id !== 'new' ? params.id : undefined);
    const detailQuery = useGetLuckyDrawDetailGeneralInfo(detailId, false);

    const applyCurrentDetail = (prev: LuckyDrawModel, incoming?: LuckyDrawModel) => {
        const next = mergeLuckyDrawGeneralSource(prev, incoming) ?? {};
        if (next.print_date_type === 'MAX_DAY_TO_PRINT') {
            delete next.last_print_date;
        }
        return next;
    };

    useEffect(() => {
        if (detailQuery.data) {
            setCurrentDetail((prev) => applyCurrentDetail(prev, detailQuery.data));
            setInitialPrintDateType((prev) => {
                if (prev || params.id === 'new') return prev
                return inferPrintDateType(detailQuery.data) ?? prev
            })
        }
    }, [detailQuery.data]);

    useEffect(() => {
        if (searchParams.step) {
            setCurrentStep(+searchParams.step)
        }
        if (searchParams.id) {
            setCurrentId(searchParams.id)
        }
    }, [searchParams])

    useEffect(() => {
        setSearchParamFunc(currentStep)
        if (params.id !== 'new') {
            setCurrentId(params.id)
        }
    }, [])

    const setSearchParamFunc = (step: number, setId?: string) => {
        const newParams: Record<string, string> = { step: step.toString() };
        if (params.id === 'new' && setId) {
            newParams.id = setId;
        }
        if (step === 2) {
            newParams.page = '1';
            newParams.limit = '10';
        }
        setSearchParams(newParams);
    }

    const backward = () => {
        setSearchParamFunc(currentStep - 1)
    }

    const saveAsDraft = (key: string, detail?: LuckyDrawModel) => {
        if (key === 'STAY') {
            const updatedId = detail?.id ?? currentId;
            setCurrentId(updatedId);
            setCurrentDetail((prev) => applyCurrentDetail(prev, detail));
            setSearchParamFunc(currentStep, updatedId);
        } else {
            history.replace(CampaignScreens.LIST_CAMPAIGN.PATH.replace(':path', 'lucky-draw'))
        }
    }

    const saveStep1 = (detail?: LuckyDrawModel) => {
        const updatedId = detail?.id ?? currentId;
        setCurrentId(updatedId);
        setCurrentDetail((prev) => applyCurrentDetail(prev, detail));
        setSearchParamFunc(2, updatedId);
    }

    const saveStep2 = () => {
        setSearchParamFunc(3, currentId)
    }

    const saveStep3 = () => {
        setSearchParamFunc(4, currentId)
    }

    const saveStep4 = (title: string) => {
        if (title === 'Edit') {
            history.replace(CampaignScreens.DETAIL_LUCKY_DRAW.PATH.replace(':path', 'detail').replace(':id', currentId))
        } else {
            history.replace(CampaignScreens.LIST_CAMPAIGN.PATH.replace(':path', 'lucky-draw'))
        }
    }

    return ({
        steps,
        currentStep,
        currentId,
        currentDetail,
        initialPrintDateType,
        backward,
        saveAsDraft,
        saveStep1,
        saveStep2,
        saveStep3,
        saveStep4
    })
}